import React, { useState } from 'react';
import { 
  Plus, 
  Search, 
  ChevronRight, 
  ChevronDown, 
  Folder, 
  Hash, 
  Edit2, 
  Trash2,
  AlertTriangle
} from 'lucide-react';
import { AccountingAccount, AccountingCategory, Account, PlannedConcept } from '../../types';
import { AccountFormModal } from './AccountFormModal';
import { deleteAccountingAccount } from '../../services/accountingService';

interface ChartViewProps {
  accounts: AccountingAccount[];
  userId: string;
  financeAccounts: Account[];
  plannedConcepts: PlannedConcept[];
}

export const ChartView: React.FC<ChartViewProps> = ({ accounts, userId, financeAccounts, plannedConcepts }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCodes, setExpandedCodes] = useState<Set<string>>(new Set(['1', '2', '3', '4', '5']));
  const [showForm, setShowForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState<AccountingAccount | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const toggleExpand = (code: string) => {
    const next = new Set(expandedCodes);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    setExpandedCodes(next);
  };

  const filteredAccounts = accounts.filter(acc => 
    acc.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    acc.code.includes(searchTerm)
  );

  const getChildren = (parentId: string | null) => {
    return accounts.filter(acc => acc.parentId === parentId).sort((a, b) => a.code.localeCompare(b.code));
  };

  const calculateBalance = (acc: AccountingAccount): number => {
    let balance = 0;
    
    // First, add its own linked finance balance if any
    if (acc.linkedFinanceAccountId) {
      const financeAcc = financeAccounts.find(fa => fa.id === acc.linkedFinanceAccountId);
      if (financeAcc) {
        balance += financeAcc.balance;
      }
    }

    // Then recursively sum the children
    const children = getChildren(acc.id);
    for (const child of children) {
      balance += calculateBalance(child);
    }

    return balance;
  };

  const handleDelete = async (id: string) => {
    const hasChildren = accounts.some(a => a.parentId === id);
    if (hasChildren) {
      alert("No se puede eliminar una cuenta que tiene subcuentas.");
      return;
    }
    
    try {
      await deleteAccountingAccount(userId, id);
      setDeletingId(null);
    } catch (e) {
      console.error(e);
      alert("Error al eliminar la cuenta.");
    }
  };

  const renderAccount = (acc: AccountingAccount) => {
    const children = getChildren(acc.id);
    const isExpanded = expandedCodes.has(acc.id) || searchTerm.length > 0;
    const hasChildren = children.length > 0;

    return (
      <div key={acc.id} className="select-none">
        <div 
          onClick={() => hasChildren && toggleExpand(acc.id)}
          className={`group flex items-center justify-between p-2 rounded-xl transition-all ${
            hasChildren ? 'cursor-pointer' : ''
          } ${
            acc.parentId === null ? 'bg-zinc-800/20 mb-1 border border-zinc-800/50' : 'hover:bg-zinc-800/30'
          }`}
          style={{ marginLeft: `${(acc.level - 1) * 24}px` }}
        >
          <div className="flex items-center gap-3 overflow-hidden">
            <div 
              className={`p-1 rounded transition-colors ${!hasChildren ? 'invisible' : 'text-zinc-400 group-hover:text-white'}`}
            >
              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </div>
            <span className="font-mono text-[11px] text-zinc-500 w-20 shrink-0">{acc.code}</span>
            <div className="flex items-center gap-2 overflow-hidden">
              {hasChildren ? (
                <Folder size={16} className={acc.parentId === null ? 'text-indigo-400' : 'text-zinc-500'} />
              ) : (
                <Hash size={16} className="text-zinc-600" />
              )}
              <span className={`text-sm truncate ${acc.parentId === null ? 'font-bold uppercase tracking-tight text-white' : 'text-zinc-300'}`}>
                {acc.name}
              </span>
              {acc.isSelectable && (
                <span className="text-[9px] bg-zinc-800 text-zinc-500 px-1 py-0.5 rounded border border-zinc-700 font-bold uppercase">
                  Imputable
                </span>
              )}
              {(acc.linkedFinanceAccountId || acc.linkedPlannedConceptId) && (
                <span className="text-[9px] bg-indigo-500/10 text-indigo-400 px-1 py-0.5 rounded border border-indigo-500/20 font-bold uppercase">
                  Vinculado
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="text-right mr-2">
              <div className="text-[10px] text-zinc-500 font-mono">
                {new Intl.NumberFormat('es-PY', { style: 'currency', currency: 'PYG' }).format(
                  calculateBalance(acc)
                )}
              </div>
              {acc.linkedFinanceAccountId ? (
                <div className="text-[8px] text-zinc-600 font-bold uppercase tracking-wider">Saldo Financiero</div>
              ) : (
                <div className="text-[8px] text-zinc-600 font-bold uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity">Subtotal</div>
              )}
            </div>
            
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
            <button 
              onClick={() => { setEditingAccount(null); setShowForm(true); }} // This should ideally pre-set the parent
              className="p-1.5 text-zinc-500 hover:text-white transition-colors"
              title="Añadir Subcuenta"
            >
              <Plus size={14} />
            </button>
            <button 
              onClick={() => { setEditingAccount(acc); setShowForm(true); }} 
              className="p-1.5 text-zinc-500 hover:text-white transition-colors"
              title="Editar"
            >
              <Edit2 size={14} />
            </button>
            {deletingId === acc.id ? (
              <div className="flex items-center gap-1 bg-red-500/10 rounded-lg overflow-hidden border border-red-500/20 ml-2">
                <button onClick={() => handleDelete(acc.id)} className="px-2 py-1 text-[10px] font-bold text-red-400 hover:bg-red-500 hover:text-black">
                  ¿Borrar?
                </button>
                <button onClick={() => setDeletingId(null)} className="px-2 py-1 text-zinc-400 hover:text-white">
                  Esc
                </button>
              </div>
            ) : (
              <button 
                onClick={() => setDeletingId(acc.id)} 
                className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors"
                title="Eliminar"
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        </div>
      </div>
        
        {isExpanded && hasChildren && (
          <div className="mt-1">
            {children.map(renderAccount)}
          </div>
        )}
      </div>
    );
  };

  const rootAccounts = accounts.filter(acc => acc.parentId === null).sort((a, b) => a.code.localeCompare(b.code));

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-3xl overflow-hidden shadow-2xl">
      <div className="p-6 border-b border-zinc-800 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
          <input 
            type="text"
            placeholder="Buscar por código o nombre..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-2xl pl-10 pr-4 py-2 text-white text-sm focus:outline-none focus:border-indigo-500 transition-colors"
          />
        </div>
        <button 
          onClick={() => { setEditingAccount(null); setShowForm(true); }}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl font-bold flex items-center justify-center gap-2 transition-all"
        >
          <Plus size={18} />
          <span className="hidden md:inline">Nueva Cuenta</span>
        </button>
      </div>

      <div className="p-6 min-h-[400px]">
        {rootAccounts.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-20 text-center text-zinc-500 gap-4">
            <LayoutGrid size={48} className="opacity-20" />
            <p>No hay cuentas definidas. Carga una plantilla o crea una nueva.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {rootAccounts.map(renderAccount)}
          </div>
        )}
      </div>

      {showForm && (
        <AccountFormModal 
          onClose={() => setShowForm(false)} 
          userId={userId} 
          accounts={accounts}
          financeAccounts={financeAccounts}
          plannedConcepts={plannedConcepts}
          editingAccount={editingAccount || undefined} 
        />
      )}
    </div>
  );
};

const LayoutGrid = ({ size, className }: any) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <rect width="7" height="7" x="3" y="3" rx="1" />
    <rect width="7" height="7" x="14" y="3" rx="1" />
    <rect width="7" height="7" x="14" y="14" rx="1" />
    <rect width="7" height="7" x="3" y="14" rx="1" />
  </svg>
);
